/** @jsx isolateComponent */
import { useState } from 'react';

import { Animation, Colors, Link, Shadows } from '@zapier/design-system';
import {
  createKeyHandler,
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

// Copy/pasted from `design-system` because flowgen bug
export type AnchorTarget = '_self' | '_blank' | '_parent' | '_top';

type ListItemProps = {
  /** Content of list item */
  children: React.ReactNode;
  /** used to render as a Link */
  href?: string;
  /** true to supress focus / hover styles */
  isReadOnly?: boolean;
  /** true to style as selected */
  isSelected?: boolean;
  /** optional click handler */
  onClick?: () => void;
  /** size to render */
  size: 'small' | 'medium' | 'large';
  /** Anchor target if is link */
  target?: AnchorTarget;
};

const Styles = labelStyles(__filename, {
  root: (isFocused: boolean, props: ListItemProps) => [
    {
      backgroundColor: Colors.grey000,
      border: `1px solid transparent`,
      borderRadius: 10,
      padding: '15px 20px',
      transition: Animation.transitionValue,
    },
    isFocused &&
      withModifier('isFocused', {
        backgroundColor: Colors.white,
        border: `1px solid ${Colors.grey200}`,
        boxShadow: Shadows.elevation5,
      }),
    props.isSelected &&
      withModifier('isFocused', {
        backgroundColor: Colors.white,
        border: `1px solid ${Colors.blue500}`,
      }),
    props.size === 'small' &&
      withModifier('small', {
        padding: 15,
      }),
    (props.onClick || props.href) &&
      withModifier('clickable', {
        cursor: 'pointer',
      }),
  ],
});

const ListItem = (props: ListItemProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const focusHandlers = props.isReadOnly
    ? {}
    : {
        onBlur: () => setIsFocused(false),
        onFocus: () => setIsFocused(true),
        onMouseEnter: () => setIsFocused(true),
        onMouseLeave: () => setIsFocused(false),
      };

  const clickHandlers =
    !props.href && props.onClick
      ? {
          onClick: props.onClick,
          onKeyDown: createKeyHandler(['enter', 'space'], () => {
            if (props.onClick) {
              props.onClick();
            }
          }),
          role: 'button',
          tabIndex: 0,
        }
      : {};

  const item = (
    <article
      css={Styles.root(isFocused, props)}
      {...clickHandlers}
      {...(props.href ? {} : focusHandlers)}
    >
      {props.children}
    </article>
  );

  return props.href ? (
    <Link href={props.href} target={props.target} {...focusHandlers}>
      {item}
    </Link>
  ) : (
    item
  );
};

ListItem.defaultProps = {
  isReadOnly: false,
};

export default ListItem;
